import { useMemo, useState } from "react";
import { Button, Checkbox, Divider, Group, ScrollArea, Text } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { toast } from "react-hot-toast";
import { parseFullName } from "../../helpers/helpers";

const COMPANY = "4CAMPS";

function escapeVCardValue(value) {
  return String(value ?? "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

function buildVCard(person, appendCompanyToLastName) {
  const firstName = person.firstName ?? "";
  let lastName = person.lastName ?? "";

  if (appendCompanyToLastName) {
    lastName = lastName ? `${lastName} ${COMPANY}` : COMPANY;
  }

  const fullName = `${firstName} ${lastName}`.trim();

  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`,
    `FN:${escapeVCardValue(fullName)}`,
  ];

  if (person.phone) {
    lines.push(`TEL;TYPE=CELL:${escapeVCardValue(person.phone)}`);
  }

  if (!appendCompanyToLastName) {
    lines.push(`ORG:${escapeVCardValue(COMPANY)}`);
  }

  lines.push(`NOTE:${escapeVCardValue(COMPANY)}`);
  lines.push("END:VCARD");

  return lines.join("\r\n");
}

export default function ExportContactsModal({ context, id, innerProps }) {
  const staff = innerProps.staff ?? [];

  const [selected, setSelected] = useState([]);
  const [appendCompanyToLastName, setAppendCompanyToLastName] = useState(false);

  const allValues = useMemo(() => staff.map((person) => String(person.id)), [staff]);
  const allSelected = selected.length > 0 && selected.length === allValues.length;
  const partiallySelected = selected.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelected(allSelected ? [] : allValues);
  };

  const download = () => {
    const selectedStaff = staff.filter((person) => selected.includes(String(person.id)));
    if (selectedStaff.length === 0) return;

    const content = selectedStaff.map((person) => buildVCard(person, appendCompanyToLastName)).join("\r\n");

    const blob = new Blob([content], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = "personal-4camps.vcf";
    downloadLink.click();
    URL.revokeObjectURL(url);

    toast.success(`Staženo ${selectedStaff.length} kontaktů.`);
    context.closeModal(id);
  };

  return (
    <>
      <Text size="sm" c="dimmed" mb="sm">
        Vyber lidi z personálu, jejichž kontakt chceš stáhnout jako VCF soubor.
      </Text>

      <Checkbox
        label="Vybrat všechny"
        checked={allSelected}
        indeterminate={partiallySelected}
        onChange={toggleAll}
        mb="xs"
      />

      <Divider mb="xs" />

      <ScrollArea.Autosize mah={320} mb="md">
        <Checkbox.Group value={selected} onChange={setSelected}>
          <div className="flex flex-col gap-2">
            {staff.map((person) => (
              <Checkbox
                key={person.id}
                value={String(person.id)}
                label={
                  <div className="flex flex-col">
                    <Text size="sm">{parseFullName(person)}</Text>
                    <Text size="xs" c="dimmed">
                      {person.phone}
                      {person.position ? ` • ${person.position}` : ""}
                    </Text>
                  </div>
                }
              />
            ))}
          </div>
        </Checkbox.Group>
      </ScrollArea.Autosize>

      <Divider mb="xs" />

      <Checkbox
        label={`Přidat "${COMPANY}" za příjmení`}
        description={`Místo pole Firma se "${COMPANY}" připojí přímo za příjmení kontaktu.`}
        checked={appendCompanyToLastName}
        onChange={(e) => setAppendCompanyToLastName(e.currentTarget.checked)}
        mb="md"
      />

      <Group grow={1}>
        <Button
          leftSection={<IconDownload size={18} stroke={1.5} />}
          disabled={selected.length === 0}
          onClick={download}>
          Stáhnout VCF{selected.length > 0 ? ` (${selected.length})` : ""}
        </Button>
      </Group>
    </>
  );
}
